import {
  LOAD_COLLECTION_COUNTS,
  LOAD_COLLECTIONS_ROUTINE,
  SELECT_COLLECTION_ROUTINE,
  ADD_COLLECTION_ROUTINE,
  ADD_COLLECTION_ASSET_ROUTINE,
  DELETE_COLLECTION_ROUTINE,
  DELETE_COLLECTION_ASSET_ROUTINE,
  UPDATE_COLLECTION_ROUTINE,
  RELOAD_ASSET_TAGS_ROUTINE,
  BULK_MOVE_ASSETS_ROUTINE,
  SELECT_EXISTING_HASHTAG_ROUTINE,
  CHANGE_LIBRARY_PAGE,
  DUPLICATE_ASSETS_ROUTINE,
} from "constants/ActionTypes";
import { createReducer } from "@reduxjs/toolkit";
import { normalizeEntity } from "reducers/reducerUtils";
import remove from "lodash/remove";
import sortBy from "lodash/sortBy";
import forEach from "lodash/forEach";

const initialState = {
  loading: false,
  byId: {},
  allIds: [],
  selectedIds: [],
  tags: [],
  error: false,
};

const loadCollections = (state, action) => {
  const { collections } = action.payload;
  if (!collections) {
    return state;
  }

  const { byId, allIds } = normalizeEntity(collections);
  return {
    ...state,
    byId,
    allIds,
    error: false,
    loading: false,
  };
};

const collectionsReducer = createReducer(initialState, (builder) => {
  builder.addCase(LOAD_COLLECTIONS_ROUTINE.REQUEST, (state) => {
    return {
      ...state,
      loading: true,
    };
  });
  builder.addCase(LOAD_COLLECTIONS_ROUTINE.SUCCESS, loadCollections);
  builder.addCase(LOAD_COLLECTION_COUNTS, (state, action) => {
    const { counts } = action.payload;
    counts.forEach(({ _id, asset_count }) => {
      if (state.byId[_id]) {
        const pendingDelta = state.byId[_id]._assetCountDelta ?? 0;
        state.byId[_id].asset_count = asset_count + pendingDelta;
        state.byId[_id]._assetCountDelta = 0;
      }
    });
    return state;
  });
  builder.addCase(LOAD_COLLECTIONS_ROUTINE.FAILURE, (state) => {
    return {
      ...state,
      error: true,
      loading: false,
    };
  });
  builder.addCase(SELECT_EXISTING_HASHTAG_ROUTINE.TRIGGER, (state, action) =>
    initCollectionSidebar(state, action)
  );
  builder.addCase(CHANGE_LIBRARY_PAGE, (state, action) =>
    initCollectionSidebar(state, action)
  );
  builder.addCase(SELECT_COLLECTION_ROUTINE.REQUEST, (state, action) => {
    const { id } = action.payload;
    if (!id) {
      return state;
    }
    return {
      ...state,
      selectedIds: [id],
    };
  });
  builder.addCase(RELOAD_ASSET_TAGS_ROUTINE.SUCCESS, (state, action) =>
    setTags(state, action)
  );
  builder.addCase(SELECT_COLLECTION_ROUTINE.SUCCESS, (state, action) =>
    setTags(state, action)
  );
  builder.addCase(ADD_COLLECTION_ROUTINE.SUCCESS, (state, action) =>
    addCollection(state, action)
  );
  builder.addCase(DELETE_COLLECTION_ROUTINE.SUCCESS, (state, action) => {
    const { id } = action.payload;
    remove(state.allIds, (collectionId) => collectionId === id);
    remove(state.selectedIds, (selectedId) => selectedId === id);
    delete state.byId[id];
  });
  builder.addCase(UPDATE_COLLECTION_ROUTINE.SUCCESS, (state, action) => {
    const { collection } = action.payload;
    state.byId[collection._id] = collection;
    return state;
  });
  builder.addCase(DELETE_COLLECTION_ASSET_ROUTINE.SUCCESS, (state, action) => {
    if (state.selectedIds.length > 1) {
      return;
    }
    const id = state.selectedIds[0];
    updateAssetCount(state.byId[id], -1);
    return state;
  });
  builder.addCase(ADD_COLLECTION_ASSET_ROUTINE.SUCCESS, (state, action) => {
    const id = action.payload.collectionId || state.selectedIds[0];
    const { assetsAdded } = action.payload;
    updateAssetCount(state.byId[id], assetsAdded);
    return state;
  });
  builder.addCase(BULK_MOVE_ASSETS_ROUTINE.SUCCESS, (state, action) => {
    const { assets, toCollectionId, fromCollectionId } = action.payload;
    if (state.byId[toCollectionId]) {
      updateAssetCount(state.byId[toCollectionId], assets.length);
    }
    if (state.byId[fromCollectionId]) {
      updateAssetCount(state.byId[fromCollectionId], -assets.length);
    }
    return state;
  });
  builder.addCase(DUPLICATE_ASSETS_ROUTINE.SUCCESS, (state) => {
    return state;
  });
});

const initCollectionSidebar = (state, action) => {
  return {
    ...state,
    selectedIds: [],
    tags: [],
  };
};

const addCollection = (state, action) => {
  let { collection } = action.payload;
  collection.asset_count = collection.asset_count ?? 0;
  collection._assetCountDelta = 0;
  state.byId[collection._id] = collection;
  state.allIds.push(collection._id);
  return state;
};

const updateAssetCount = (collection, delta) => {
  if (!collection) {
    return;
  }

  collection._assetCountDelta = (collection._assetCountDelta ?? 0) + delta;

  if (collection.asset_count != null) {
    collection.asset_count += delta;
  }
};

const setTags = (state, action) => {
  let { tags } = action.payload;
  const sortedTags = sortBy(tags, ["value"]);
  return {
    ...state,
    tags: sortedTags,
  };
};

export const formatTags = (tags) => {
  let formattedTags = [];

  forEach(tags, (tag) => {
    formattedTags.push({
      count: tag.count,
      value: tag.tag,
      label: tag.tag,
    });
  });
  return formattedTags;
};

export default collectionsReducer;
